import { CheckCircle, XCircle } from 'lucide-react'
import type { Exercise } from '@/types'
import { splitByBlank, hasBlankMarker } from '@/lib/exerciseUtils'
import EditableText from '@/components/edit/EditableText'
import { useEditMode } from '@/contexts/EditModeContext'
import { useAuth } from '@/contexts/AuthContext'
import { saveField } from '@/lib/editApi'

interface Props {
  exercise: Exercise
  textAnswer: string
  onChangeAnswer: (answer: string) => void
  isSubmitted: boolean
  isCorrect: boolean
  onExerciseUpdated?: (updated: Exercise) => void
}

export default function FillBlankExercise({ exercise, textAnswer, onChangeAnswer, isSubmitted, isCorrect, onExerciseUpdated }: Props) {
  const { isEditMode } = useEditMode()
  const { profile } = useAuth()

  const saveCorrectAnswer = async (newText: string) => {
    if (!profile?.id) return { error: new Error('No autenticado') }
    const { error } = await saveField({
      table: 'exercises', id: exercise.id, field: 'correct_answer',
      oldValue: exercise.correct_answer || '', newValue: newText, editedBy: profile.id
    })
    if (!error) onExerciseUpdated?.({ ...exercise, correct_answer: newText })
    return { error }
  }

  const saveQuestion = async (newText: string) => {
    if (!profile?.id) return { error: new Error('No autenticado') }
    const { error } = await saveField({
      table: 'exercises', id: exercise.id, field: 'question',
      oldValue: exercise.question || '', newValue: newText, editedBy: profile.id
    })
    if (!error) onExerciseUpdated?.({ ...exercise, question: newText })
    return { error }
  }

  const question = exercise.question || ''

  // Si la pregunta tiene marcador de espacio
  if (hasBlankMarker(question)) {
    const parts = splitByBlank(question)

    return (
      <div className="space-y-4">
        {/* En modo edición mostramos la pregunta COMPLETA con el ___
            como texto editable, así la profe puede mover el blank o
            cambiar el texto sin romper la estructura. En modo lectura
            (alumno) mostramos la oración partida con el input inline. */}
        {isEditMode ? (
          <div className="p-6 bg-amber-50/50 border-2 border-dashed border-amber-300 rounded-xl">
            <p className="text-xs text-amber-700 mb-2 font-medium">
              Pregunta original (usá <code className="px-1 bg-amber-200 rounded">___</code> para indicar dónde va el espacio en blanco)
            </p>
            <EditableText
              as="div"
              value={question}
              customSave={saveQuestion}
              placeholder="(sin pregunta)"
              multiline
              className="text-xl leading-relaxed text-gray-900 block"
            />
          </div>
        ) : (
          <div className="p-6 bg-gray-50 rounded-xl">
            <p className="text-xl leading-relaxed text-gray-900 flex flex-wrap items-baseline gap-1">
              {parts.map((part, i) => (
                <span key={i} className="inline-flex items-baseline gap-1">
                  <span>{part}</span>
                  {i < parts.length - 1 && (
                    <span className="inline-block relative">
                      <input
                        type="text"
                        value={textAnswer}
                        onChange={(e) => onChangeAnswer(e.target.value)}
                        disabled={isSubmitted}
                        placeholder="..."
                        className={`w-32 px-2 py-1 text-center text-xl font-medium border-b-3 bg-transparent outline-none transition-colors ${
                          isSubmitted
                            ? isCorrect
                              ? 'border-green-500 text-green-700'
                              : 'border-red-500 text-red-700'
                            : 'border-blue-400 text-blue-700 focus:border-blue-600'
                        }`}
                        autoFocus
                      />
                      {isSubmitted && (
                        <span className="absolute -right-7 top-1/2 -translate-y-1/2">
                          {isCorrect
                            ? <CheckCircle className="w-5 h-5 text-green-500" />
                            : <XCircle className="w-5 h-5 text-red-500" />
                          }
                        </span>
                      )}
                    </span>
                  )}
                </span>
              ))}
            </p>
          </div>
        )}

        {/* Mostrar respuesta correcta si fallo */}
        {isSubmitted && !isCorrect && (
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-gray-600">
              Respuesta correcta: <span className="font-bold text-green-700">{exercise.correct_answer}</span>
            </p>
          </div>
        )}

        {isEditMode && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-xs font-medium text-amber-700 mb-1">Respuesta correcta:</p>
            <EditableText
              as="div"
              value={exercise.correct_answer}
              customSave={saveCorrectAnswer}
              placeholder="(sin respuesta correcta)"
              className="text-sm font-semibold text-amber-900"
            />
          </div>
        )}
      </div>
    )
  }

  // Fallback: si no hay marcador, mostrar input simple
  return (
    <div className="space-y-4">
      <EditableText
        as="p"
        value={question}
        customSave={saveQuestion}
        placeholder="(sin pregunta)"
        multiline
        className="text-lg text-gray-700 mb-4 block"
      />
      <input
        type="text"
        value={textAnswer}
        onChange={(e) => onChangeAnswer(e.target.value)}
        disabled={isSubmitted}
        placeholder="Escribe tu respuesta..."
        className={`w-full p-4 rounded-xl border-2 text-lg ${
          isSubmitted
            ? isCorrect ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'
            : 'border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200'
        }`}
      />
      {isSubmitted && !isCorrect && (
        <p className="text-sm text-gray-600">
          Respuesta correcta: <span className="font-medium text-green-600">{exercise.correct_answer}</span>
        </p>
      )}

      {isEditMode && (
        <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
          <p className="text-xs font-medium text-amber-700 mb-1">Respuesta correcta:</p>
          <EditableText
            as="div"
            value={exercise.correct_answer}
            customSave={saveCorrectAnswer}
            placeholder="(sin respuesta correcta)"
            className="text-sm font-semibold text-amber-900"
          />
        </div>
      )}
    </div>
  )
}
